import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/store';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import Entities from '@/pages/Entities';
import ChartOfAccounts from '@/pages/ChartOfAccounts';
import TrialBalances from '@/pages/TrialBalances';
import Consolidation from '@/pages/Consolidation';
import Reports from '@/pages/Reports';
import Settings from '@/pages/Settings';
import Intercompany from '@/pages/Intercompany';
import IcDisputes from '@/pages/IcDisputes';
import EntityCodeMapping from '@/pages/EntityCodeMapping';
import TaxComputation from '@/pages/TaxComputation';
import TransferPricing from '@/pages/TransferPricing';
import AnomalyDashboard from '@/pages/AnomalyDashboard';
import NLQuery from '@/pages/NLQuery';
import ApprovalWorkflows from '@/pages/ApprovalWorkflows';
import XBRLManagement from '@/pages/XBRLManagement';
import ScenarioModeling from '@/pages/ScenarioModeling';
import ESGDashboard from '@/pages/ESGDashboard';
import Forecasting from '@/pages/Forecasting';
import MultiGAAP from '@/pages/MultiGAAP';
import Marketplace from '@/pages/Marketplace';
import BlockchainAudit from '@/pages/BlockchainAudit';
import DataQuality from '@/pages/DataQuality';
import PredictiveClose from '@/pages/PredictiveClose';
import KPIDashboard from '@/pages/KPIDashboard';
import CloseStatus from '@/pages/CloseStatus';
import RulesEngine from '@/pages/RulesEngine';
import BudgetForecast from '@/pages/BudgetForecast';
import AIMappingReview from '@/pages/AIMappingReview';
import EntityCOA from '@/pages/EntityCOA';
import FinancialReports from '@/pages/FinancialReports';
import FinancialValidation from '@/pages/FinancialValidation';
import IcpPartners from '@/pages/IcpPartners';
import JournalEntries from '@/pages/JournalEntries';

// ─── Protected Route ────────────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-700 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ─── App ────────────────────────────────────────────────────────────────────

export default function App() {
  const { loadUser, isAuthenticated, token } = useAuthStore();

  useEffect(() => {
    if (token) {
      loadUser();
    }
  }, [token, loadUser]);

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        }
      />

      {/* Protected routes with Layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/entities" replace />} />
        <Route path="entities" element={<Entities />} />
        <Route path="icp-partners" element={<IcpPartners />} />
        <Route path="entity-code-mapping" element={<EntityCodeMapping />} />
        <Route path="chart-of-accounts" element={<ChartOfAccounts />} />
        <Route path="entity-coa" element={<EntityCOA />} />
        <Route path="trial-balances" element={<TrialBalances />} />
        <Route path="financial-reports" element={<FinancialReports />} />
        <Route path="financial-validation" element={<FinancialValidation />} />
        <Route path="journal-entries" element={<JournalEntries />} />
        <Route path="consolidation" element={<Consolidation />} />
        <Route path="reports" element={<Reports />} />
        {/* <Route path="audit" element={<AuditTrail />} /> */}
        <Route path="intercompany" element={<Intercompany />} />
        <Route path="ic-disputes" element={<IcDisputes />} />
        <Route path="tax" element={<TaxComputation />} />
        <Route path="transfer-pricing" element={<TransferPricing />} />
        <Route path="anomalies" element={<AnomalyDashboard />} />
        <Route path="nl-query" element={<NLQuery />} />
        <Route path="approvals" element={<ApprovalWorkflows />} />
        <Route path="xbrl" element={<XBRLManagement />} />
        <Route path="scenarios" element={<ScenarioModeling />} />
        <Route path="esg" element={<ESGDashboard />} />
        <Route path="forecasting" element={<Forecasting />} />
        <Route path="multi-gaap" element={<MultiGAAP />} />
        <Route path="marketplace" element={<Marketplace />} />
        <Route path="blockchain-audit" element={<BlockchainAudit />} />
        <Route path="data-quality" element={<DataQuality />} />
        <Route path="predictive-close" element={<PredictiveClose />} />
        <Route path="kpi" element={<KPIDashboard />} />
        <Route path="close-status" element={<CloseStatus />} />
        <Route path="rules" element={<RulesEngine />} />
        <Route path="budget" element={<BudgetForecast />} />
        <Route path="ai-mapping" element={<AIMappingReview />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
